import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { URL_ACCOUNT_REGISTRATION, URL_SIGNIN } from "@seek/adv-constants";
import { useTranslations } from "@vocab/react";
import { Box, MenuRenderer, MenuItemLink, Strong, IconTick, IconLanguage, Text, IconChevron, Inline, ButtonLink } from "braid-design-system";
import { analyticsEvents, analyticsLanguageSelectorEvents, analyticsDesktopHeaderEvents } from "../../../EmployerFooter/AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import { useEmployerHeaderContext, testTags } from "../../EmployerHeader.mjs";
import { isPending, isAuthenticated } from "../../../EmployerFooter/EmployerFooter.mjs";
import { getCtaPath } from "../BrandedLogo/BrandedLogo.mjs";
import { translations } from "../../../../EmployerHeader/components/.vocab/index.mjs";
import { AccountDropdown } from "../AccountDropdown/AccountDropdown.mjs";
import { siteConfig, languages } from "@seek/melways-sites";
import { CustomHeaderDesktopLink } from "../Navigation/HeaderLink.mjs";
import { root, container, isReady } from "./HeaderActions.css.mjs";
const languageMapping = {
  en: "English",
  id: "Bahasa",
  th: "ภาษาไทย",
  zh: "中文"
};
const getLanguagesForSelector = (site) => {
  const siteLanguages = siteConfig[site].languages;
  const languages2 = Array.isArray(siteLanguages) ? siteLanguages.filter(isListedLanguage) : [];
  if (isAtLeastTwoLanguages(languages2)) {
    return languages2;
  }
  return;
};
const listedLanguages = ["en", "id", "th"];
const isListedLanguage = (language) => language ? listedLanguages.includes(language) : false;
const isAtLeastTwoLanguages = (languages2) => languages2.length > 1;
const getWindowPathWithoutLanguage = () => {
  if (typeof window === "undefined") {
    return "/";
  }
  let path = window.location.pathname;
  const pathArray = path.split("/");
  if (languages.find((language) => language === pathArray[1])) {
    pathArray.splice(1, 1);
    path = pathArray.join("/") || "/";
  }
  return path + window.location.search + window.location.hash;
};
const LanguageSelector = ({
  language = "en",
  site,
  urlResolver
}) => {
  const { handleInteractionTracking } = useEmployerHeaderContext();
  const languages2 = getLanguagesForSelector(site);
  if (!languages2) {
    return null;
  }
  const strippedPath = getWindowPathWithoutLanguage();
  return /* @__PURE__ */ jsx(Box, { "data-testid": "languageSelector", children: /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      reserveIconSpace: true,
      align: "right",
      offsetSpace: "small",
      width: "small",
      onOpen: () => {
        handleInteractionTracking(analyticsLanguageSelectorEvents.toggleOpen);
      },
      onClose: () => {
        handleInteractionTracking(
          analyticsLanguageSelectorEvents.toggleClosed
        );
      },
      trigger: (triggerProps, { open }) => /* @__PURE__ */ jsxs(
        Box,
        {
          userSelect: "none",
          cursor: "pointer",
          ...triggerProps,
          display: "flex",
          alignItems: "center",
          children: [
            /* @__PURE__ */ jsx(
              IconLanguage,
              {
                size: "small",
                data: { testid: "languageSelectorLabel" }
              }
            ),
            " ",
            /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(IconChevron, { direction: open ? "up" : "down" }) })
          ]
        }
      ),
      children: languages2.map((languageOption) => {
        const href = urlResolver({
          language: languageOption,
          path: strippedPath
        });
        return /* @__PURE__ */ jsx(
          MenuItemLink,
          {
            href,
            data: {
              analytics: analyticsEvents.trackSelectLanguage(
                false,
                languageOption
              )
            },
            icon: languageOption === language && /* @__PURE__ */ jsx(IconTick, { tone: "positive" }),
            onClick: () => {
              handleInteractionTracking({
                ...analyticsLanguageSelectorEvents.languageSelected,
                actionPressed: `language selected - ${languageOption}`
              });
              window.location.href = href;
            },
            children: languageOption === language ? /* @__PURE__ */ jsx(Strong, { children: languageMapping[languageOption] }) : /* @__PURE__ */ jsx(Fragment, { children: languageMapping[languageOption] })
          },
          languageOption
        );
      })
    }
  ) });
};
const HeaderActions = ({
  onToggleButtonClick,
  linkRenderer: LinkRenderer,
  id,
  language,
  site,
  identityIsAuthenticated
}) => {
  const { t } = useTranslations(translations);
  const {
    authenticationStatus,
    shouldDisplayActions,
    shouldDisplayCtaButton,
    urlResolver,
    handleInteractionTracking
  } = useEmployerHeaderContext();
  const registerURL = urlResolver({
    path: URL_ACCOUNT_REGISTRATION
  });
  const signInURL = urlResolver({
    path: URL_SIGNIN
  });
  const ctaURL = urlResolver({
    path: getCtaPath(authenticationStatus)
  });
  const authStatusKnown = !isPending(authenticationStatus);
  const shouldDisplayAccountDropdown = isAuthenticated(authenticationStatus);
  return /* @__PURE__ */ jsx(
    Box,
    {
      className: root,
      alignItems: "center",
      justifyContent: "flexEnd",
      flexGrow: 0,
      flexShrink: 0,
      display: "flex",
      children: /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            display: "flex",
            alignItems: "center",
            justifyContent: "flexEnd",
            className: [
              container,
              {
                [isReady]: authStatusKnown
              }
            ],
            "data-test": "header-actions-container",
            children: /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
              /* @__PURE__ */ jsx(
                LanguageSelector,
                {
                  language,
                  site,
                  urlResolver,
                  displayAsCompact: identityIsAuthenticated
                }
              ),
              shouldDisplayActions ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(
                  CustomHeaderDesktopLink,
                  {
                    href: signInURL,
                    "data-analytics": analyticsEvents.TRACK_SIGN_IN,
                    "data-test": testTags.SIGN_IN,
                    linkRenderer: LinkRenderer,
                    analyticsEvent: analyticsDesktopHeaderEvents.signIn,
                    children: t("Sign in")
                  }
                ),
                /* @__PURE__ */ jsx(
                  CustomHeaderDesktopLink,
                  {
                    href: registerURL,
                    "data-analytics": analyticsEvents.TRACK_REGISTRATION,
                    "data-test": testTags.REGISTER,
                    linkRenderer: LinkRenderer,
                    analyticsEvent: analyticsDesktopHeaderEvents.register,
                    children: t("Register")
                  }
                )
              ] }) : null,
              shouldDisplayAccountDropdown ? /* @__PURE__ */ jsx(
                AccountDropdown,
                {
                  id,
                  onToggleDropdown: onToggleButtonClick,
                  linkRenderer: LinkRenderer,
                  language
                }
              ) : null
            ] })
          }
        ),
        shouldDisplayCtaButton ? /* @__PURE__ */ jsx(
          ButtonLink,
          {
            variant: "ghost",
            tone: "neutral",
            size: "small",
            href: ctaURL,
            data: {
              analytics: analyticsEvents.TRACK_CREATE_JOB,
              test: testTags.CREATE_A_JOB_AD
            },
            onClick: () => {
              handleInteractionTracking(
                analyticsDesktopHeaderEvents.createAJobAd
              );
            },
            children: t("Create a job ad")
          }
        ) : null
      ] })
    }
  );
};
export {
  HeaderActions,
  getLanguagesForSelector,
  getWindowPathWithoutLanguage,
  languageMapping
};
